import {
  LEADERBOARD_POINTS_CONFIG,
  PostInteractionAction,
} from "@/src/constants/leaderboard.constant";
import { useAuth } from "@/src/contexts/auth.context";
import { LeaderboardEntry } from "@/src/models/leaderboard.model";
import { Post, ReactionType } from "@/src/models/post.model";
import {
  LEADERBOARD_QUERY_KEYS,
  useUpsertLeaderboardPoints,
} from "@/src/services/api/leaderboard/leaderboard.hooks";
import { useSendNotification } from "@/src/services/api/notification/notification.hooks";
import {
  useGetUserPostReaction,
  useReactToPostMutation,
  useUpdatePostScoreMutation,
} from "@/src/services/api/posts/posts.hooks";
import { hot } from "@/src/utils/feed-score";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export const useReactToPost = (post: Post) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const userId = user?.itemId ?? "";
  const queryClient = useQueryClient();
  const upsertPoints = useUpsertLeaderboardPoints();

  const { data: userReaction } = useGetUserPostReaction(post.ItemId, userId);
  const { mutate } = useReactToPostMutation();
  const { mutate: updateScore } = useUpdatePostScoreMutation();
  const { mutate: sendNotification } = useSendNotification();

  const handleReact = (newReaction: ReactionType | null) => {
    mutate(
      {
        postId: post.ItemId,
        userId,
        newReaction,
        previousReaction: userReaction?.type ?? null,
        currentCounts: post.reactionCount,
        existingReactionId: userReaction?.id,
        reactorName: user?.firstName + ' ' + user?.lastName,
        reactorProfileImageUrl: user?.profileImageUrl
      },
      {
        onSuccess: () => {
          if (newReaction && !userReaction?.type) {
            const action = newReaction as PostInteractionAction;
            const delta = LEADERBOARD_POINTS_CONFIG[action];
            if (delta) {
              const currentEntry =
                queryClient.getQueryData<LeaderboardEntry | null>(
                  LEADERBOARD_QUERY_KEYS.byUser(userId),
                ) ?? null;
              upsertPoints.mutate({ userId, pointsDelta: delta, currentEntry });
            }
          } else if (!newReaction && userReaction?.type) {
            const action = userReaction.type as PostInteractionAction;
            const delta = LEADERBOARD_POINTS_CONFIG[action];
            if (delta) {
              const currentEntry =
                queryClient.getQueryData<LeaderboardEntry | null>(
                  LEADERBOARD_QUERY_KEYS.byUser(userId),
                ) ?? null;
              upsertPoints.mutate({
                userId,
                pointsDelta: -delta,
                currentEntry,
              });
            }
          }

          const totalReacts = Object.values(post.reactionCount ?? {}).reduce(
            (a, b) => a + b,
            0,
          );
          const change =
            newReaction && !userReaction?.type
              ? 1
              : !newReaction && userReaction?.type
                ? -1
                : 0;

          updateScore({
            id: post.ItemId,
            score: hot(totalReacts + change, new Date(post.CreatedDate)),
          });
          sendNotification({
            userIds: [post.authorSnapshot.userId],
            denormalizedPayload: JSON.stringify({
              type: "post_reaction",
              message: t("LABEL_NOTIFY_POST_REACTION", {
                firstName: user?.firstName,
                lastName: user?.lastName,
              }),
              actorAvatar: user?.profileImageUrl,
              targetEntityId: post.ItemId,
            }),
            configuratoinName: "post-liked",
            responseKey: "post-action",
            responseValue: newReaction as string,
          });
        },
      },
    );
  };

  return { userReaction, handleReact };
};
